import axios from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// TypeScript Interfaces
export interface PickupSheetUser {
  id: number;
  name: string;
  company_name?: string;
  branch?: {
    id: number;
    name: string;
  };
}

export interface PickupSheetRider {
  id: number;
  name: string;
}

export interface PickupSheetStatus {
  id: number;
  name: string;
  badge: string;
}

export interface PickupSheetConsignment {
  id: number;
  consignment_no: string;
  tracking_number: string;
  orderId?: string;
  origin_city?: string;
  destination_city?: string;
  destinationCity?: {
    id: number;
    name: string;
  };
  receiver_name?: string;
  cod_amount: number;
  service_type?: string;
  created_at: string;
  status?: PickupSheetStatus;
  pickupsheet_status?: PickupSheetStatus;
  reason?: string;
  attachment?: string;
  user?: {
    Branch?: {
      city?: {
        name: string;
      };
      name: string;
    };
  };
}

export interface PickupSheet {
  id: number;
  unique_id: string;
  user?: PickupSheetUser;
  rider?: PickupSheetRider;
  status?: PickupSheetStatus;
  total_consignments: number;
  handover_count: number;
  created_at: string;
}

export interface PickupSheetPending {
  id: number;
  description: string;
  created_at: string;
  user?: {
    name: string;
  };
}

export interface PickupSheetDetail extends PickupSheet {
  consignments: PickupSheetConsignment[];
  total_cod_amount: number;
  pickedup_at?: string;
  pendings?: PickupSheetPending[];
}

export interface Customer {
  id: number;
  name: string;
  company_name?: string;
}

export interface ReadyConsignment {
  id: number;
  tracking_number: string;
  receiver_name: string;
  receiver_phone: string;
  origin_city?: string;
  destination_city?: string;
  cod_amount: number;
  status?: {
    id: number;
    name: string;
  };
  created_at: string;
}

export interface PickupSheetFilters {
  page?: number;
  per_page?: number;
  status?: number;
  customer_id?: number;
}

// API Functions
export const getPickupSheets = async (filters: PickupSheetFilters = {}) => {
  const params = new URLSearchParams();
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.per_page) params.append('per_page', filters.per_page.toString());
  if (filters.status) params.append('status', filters.status.toString());
  if (filters.customer_id) params.append('customer_id', filters.customer_id.toString());

  const response = await apiClient.get(`/pickup-sheets?${params.toString()}`);
  return response.data;
};

export const getPickupSheet = async (id: number) => {
  const response = await apiClient.get(`/pickup-sheets/${id}`);
  return response.data;
};

export const getCustomers = async () => {
  const response = await apiClient.get('/pickup-sheets/customers');
  return response.data;
};

export const getReadyConsignments = async () => {
  const response = await apiClient.get('/pickup-sheets/ready-consignments');
  return response.data;
};

export const createPickupSheet = async (consignmentIds: number[]) => {
  const response = await apiClient.post('/pickup-sheets', {
    consignment_ids: consignmentIds,
  });
  return response.data;
};

export const cancelPickupSheet = async (id: number) => {
  const response = await apiClient.post(`/pickup-sheets/${id}/cancel`);
  return response.data;
};

export const markAsReceivedAtHub = async (id: number) => {
  const response = await apiClient.post(`/pickup-sheets/${id}/collected`);
  return response.data;
};

export const markAsPickedUp = async (id: number, riderPin: string) => {
  const response = await apiClient.post(`/pickup-sheets/${id}/pickup`, {
    rider_pin: riderPin,
  });
  return response.data;
};

export const printPickupSheet = (id: number) => {
  const token = localStorage.getItem('token');
  const url = `${API_BASE_URL}/public/pickup-sheets/${id}/download?action=print&token=${token}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};
